import React from "react";
import { Typography, Box, useTheme } from "@mui/material";
import { tokens } from "../layout/theme";
import logo from '../../assets/images/aqualogo.png';

/**
 * Interface defining the props for the Header component.
 * @property {string} [title] - Optional title text to display in the header
 * @property {string} [subtitle] - Optional subtitle text to display below the title
 * @property {boolean} [showLogo] - Optional flag to display the Aqua logo instead of the title
 */
type Props = {
  title?: string;
  subtitle?: string;
  showLogo?: boolean;
};

/**
 * A reusable header component that can display either a title and subtitle or a logo.
 * 
 * This component provides a consistent header style across the application.
 * It supports two display modes:
 * 1. Title and subtitle text with customizable styling
 * 2. Logo display with fixed dimensions
 * 
 * @component
 * @param {Props} props - The props passed to the component
 * @param {string} [props.title] - The main title text
 * @param {string} [props.subtitle] - The subtitle text
 * @param {boolean} [props.showLogo] - Whether to show the logo instead of the title
 * 
 * @example
 * ```tsx
 * // With title and subtitle
 * <Header 
 *   title="Dashboard" 
 *   subtitle="Welcome to your dashboard" 
 * />
 * 
 * // With logo
 * <Header showLogo={true} />
 * ```
 * 
 * @remarks
 * - Uses Material-UI Typography and Box components
 * - Implements responsive design
 * - Supports theme-based color customization
 * - Maintains consistent spacing and typography
 * 
 * @returns {JSX.Element} A React component that renders a header section
 */
const Header = ({ title, subtitle, showLogo }: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <>
      <Box mb="10px">
        {showLogo ? (
          <Box
            component="img"
            src={logo}
            alt="Careblue Logo"
            sx={{
              height: 100,
              width: 'auto',
              marginBottom: 2,
            }}
          />
        ) : (
          title && (
            <Typography
              variant="h3"
              color={colors.grey[100]}
              fontWeight="bold"
            >
              {title}
            </Typography>
          )
        )}

        {subtitle && (
          <Typography
            variant="h5"
            color={colors.blueAccent[300]}
            fontWeight="bold"
            p="1em"
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default Header;
